// @ts-nocheck
/**
 * ポートフォリオ画面
 * 保有ファンド一覧、損益表示、取引履歴
 */

import React, { useState, useMemo } from 'react';
// import { getFund } from '../data/funds'; // Used internally by portfolioManager
// import { FX_PAIRS } from '../data/fxPairs'; // Not needed
import {
  getPortfolioSummary,
  getHoldingsList,
  sortHoldings,
  getTradeHistory,
  enrichTradeHistory,
  getCumulativePnL,
  getAttributeDistribution,
} from '../systems/portfolioManager';
import { getFXPositionDetails } from '../systems/fxManager';
import { getDividendTierInfo } from '../systems/fundManager';
import { useIsMobile } from '../../hooks/useMediaQuery';

const Portfolio = ({
  playerData,
  updatePlayerData,
  currentBattleNumber,
  onSelectFund,
  marketIndex,
  marketState,
}) => {
  const isMobile = useIsMobile();
  const [activeSubTab, setActiveSubTab] = useState('holdings'); // 'holdings' | 'history' | 'analysis'
  const [sortBy, setSortBy] = useState('value');

  const investmentData = playerData.investmentData || {};
  const summary = useMemo(
    () => getPortfolioSummary(investmentData, marketIndex, marketState),
    [investmentData, marketIndex, marketState]
  );
  const holdings = useMemo(
    () => sortHoldings(getHoldingsList(investmentData, currentBattleNumber, marketIndex, marketState), sortBy),
    [investmentData, currentBattleNumber, sortBy, marketIndex, marketState]
  );
  const history = useMemo(
    () => enrichTradeHistory(getTradeHistory(investmentData, { limit: 30 })),
    [investmentData]
  );
  const cumulativePnL = useMemo(
    () => getCumulativePnL(investmentData, marketIndex, marketState),
    [investmentData, marketIndex, marketState]
  );
  const attributeDistribution = useMemo(
    () => getAttributeDistribution(investmentData, marketIndex, marketState),
    [investmentData, marketIndex, marketState]
  );

  // FXポジション一覧
  const fxPositions = useMemo(() => {
    const positions = investmentData.fxPositions || [];
    return positions.map((pos) =>
      getFXPositionDetails(pos, investmentData, currentBattleNumber, marketIndex, marketState)
    );
  }, [investmentData, currentBattleNumber, marketIndex, marketState]);

  // FXポジションの合計評価額・損益
  const fxSummary = useMemo(() => {
    let totalValue = 0;
    let totalCost = 0;
    for (const pos of fxPositions) {
      totalValue += pos.estimatedValue || 0;
      totalCost += pos.margin || 0;
    }
    const totalPnl = totalValue - totalCost;
    const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;
    return { totalValue, totalCost, totalPnl, totalPnlPercent };
  }, [fxPositions]);

  // ファンド+FX合算サマリー
  const combinedSummary = useMemo(() => ({
    totalValue: summary.totalValue + fxSummary.totalValue,
    totalCost: summary.totalCost + fxSummary.totalCost,
    totalPnl: summary.totalPnl + fxSummary.totalPnl,
    totalPnlPercent: (summary.totalCost + fxSummary.totalCost) > 0
      ? ((summary.totalPnl + fxSummary.totalPnl) / (summary.totalCost + fxSummary.totalCost)) * 100
      : 0,
  }), [summary, fxSummary]);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    subTabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '8px',
    },
    subTab: {
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      transition: 'all 0.2s',
    },
    subTabActive: {
      backgroundColor: '#4fc3f7',
      color: '#000',
    },
    subTabInactive: {
      backgroundColor: '#333',
      color: '#888',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: '12px',
    },
    summaryCard: {
      padding: '12px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      textAlign: 'center',
    },
    summaryLabel: {
      fontSize: '11px',
      color: '#888',
      marginBottom: '4px',
    },
    summaryValue: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    holdingsSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    sortRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
    },
    sortLabel: {
      fontSize: '12px',
      color: '#888',
    },
    sortSelect: {
      padding: '6px 10px',
      backgroundColor: '#16213e',
      border: '1px solid #333',
      borderRadius: '4px',
      color: '#e0e0e0',
      fontSize: '12px',
    },
    holdingCard: {
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '10px' : '14px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid transparent',
    },
    holdingIcon: {
      fontSize: '24px',
      marginRight: '12px',
    },
    holdingInfo: {
      flex: 1,
      minWidth: 0,
    },
    holdingName: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#e0e0e0',
      marginBottom: '2px',
    },
    holdingDetails: {
      fontSize: '12px',
      color: '#888',
    },
    holdingValue: {
      textAlign: 'right',
    },
    holdingAmount: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#e0e0e0',
    },
    holdingPnl: {
      fontSize: '12px',
    },
    holdingExpiry: {
      fontSize: '11px',
      marginTop: '2px',
    },
    expiryWarning: {
      color: '#ff9800',
    },
    expiryNormal: {
      color: '#888',
    },
    dividendInfo: {
      fontSize: '11px',
      marginTop: '2px',
    },
    dividendTierShort: {
      color: '#888',
    },
    dividendTierMid: {
      color: '#4fc3f7',
    },
    dividendTierLong: {
      color: '#4caf50',
    },
    fxSection: {
      marginTop: '24px',
      paddingTop: '16px',
      borderTop: '1px solid #333',
    },
    fxSectionTitle: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#ff9800',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    fxCard: {
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '10px' : '14px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      marginBottom: '8px',
      border: '1px solid transparent',
    },
    fxDirection: {
      fontSize: '11px',
      padding: '2px 6px',
      borderRadius: '4px',
      marginLeft: '8px',
    },
    fxDirectionLong: {
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      color: '#4caf50',
    },
    fxDirectionShort: {
      backgroundColor: 'rgba(244, 67, 54, 0.2)',
      color: '#f44336',
    },
    emptyState: {
      padding: '40px',
      textAlign: 'center',
      color: '#666',
    },
    historyList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    historyItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
    },
    historyIcon: {
      fontSize: '20px',
      marginRight: '10px',
    },
    historyInfo: {
      flex: 1,
    },
    historyAction: {
      fontSize: '13px',
      fontWeight: 'bold',
      marginBottom: '2px',
    },
    historyDate: {
      fontSize: '11px',
      color: '#888',
    },
    historyAmount: {
      textAlign: 'right',
    },
    historyPrice: {
      fontSize: '13px',
      color: '#e0e0e0',
    },
    historyPnl: {
      fontSize: '11px',
    },
    analysisSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    analysisCard: {
      padding: '16px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
    },
    analysisTitle: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#4fc3f7',
      marginBottom: '12px',
    },
    distributionBar: {
      display: 'flex',
      height: '24px',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '8px',
    },
    distributionLegend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
    },
    legendColor: {
      width: '12px',
      height: '12px',
      borderRadius: '2px',
    },
    statRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
    },
    statLabel: {
      color: '#888',
    },
    statValue: {
      fontWeight: 'bold',
    },
  };

  // 属性カラー
  const attributeColors = {
    '炎': '#f44336',
    '水': '#2196f3',
    '光': '#ffc107',
    '闇': '#9c27b0',
    '原始': '#4caf50',
    '未来': '#00bcd4',
  };

  // 保有一覧
  const renderHoldings = () => {
    const hasFunds = holdings.length > 0;
    const hasFX = fxPositions.length > 0;

    if (!hasFunds && !hasFX) {
      return (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <div>保有資産がありません</div>
          <div style={{ fontSize: '13px', marginTop: '8px' }}>
            ファンドの購入またはFX取引を開始しましょう
          </div>
        </div>
      );
    }

    return (
      <div style={styles.holdingsSection}>
        {/* ファンド保有 */}
        {hasFunds && (
          <>
            <div style={styles.sortRow}>
              <span style={styles.sortLabel}>ファンド並び替え:</span>
              <select
                style={styles.sortSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="value">評価額</option>
                <option value="pnl">損益額</option>
                <option value="pnlPercent">損益率</option>
                <option value="daysRemaining">残り期間</option>
              </select>
            </div>

            {holdings.map((holding) => (
              <div
                key={holding.id}
                style={styles.holdingCard}
                onClick={() => onSelectFund(holding.fund)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#4fc3f7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={styles.holdingIcon}>{holding.fund?.icon || '📊'}</div>
                <div style={styles.holdingInfo}>
                  <div style={styles.holdingName}>{holding.fund?.name || '不明'}</div>
                  <div style={styles.holdingDetails}>
                    {holding.units}口 × {holding.currentPrice.toLocaleString()}G
                  </div>
                </div>
                <div style={styles.holdingValue}>
                  <div style={styles.holdingAmount}>
                    {holding.value.toLocaleString()}G
                  </div>
                  <div
                    style={{
                      ...styles.holdingPnl,
                      color: holding.pnl >= 0 ? '#4caf50' : '#f44336',
                    }}
                  >
                    {holding.pnl >= 0 ? '+' : ''}{holding.pnl.toLocaleString()}G
                    ({holding.pnlPercent >= 0 ? '+' : ''}{holding.pnlPercent.toFixed(1)}%)
                  </div>
                  {holding.isNoExpiry ? (
                    // 現物保有（期限なし）: 配当ティア情報を表示
                    (() => {
                      const tierInfo = getDividendTierInfo(holding, currentBattleNumber);
                      const tierStyle = tierInfo.tier === '長期'
                        ? styles.dividendTierLong
                        : tierInfo.tier === '中期'
                          ? styles.dividendTierMid
                          : styles.dividendTierShort;
                      return (
                        <div style={{ ...styles.dividendInfo, ...tierStyle }}>
                          {tierInfo.tier === '長期' && '🌟 '}
                          {tierInfo.tier === '中期' && '📈 '}
                          {tierInfo.tier}保有 ({tierInfo.holdingDays}日目)
                          {tierInfo.rate > 0
                            ? ` 配当${(tierInfo.rate * 100).toFixed(1)}%/日`
                            : tierInfo.daysToNextTier !== null
                              ? ` あと${tierInfo.daysToNextTier}日で配当開始`
                              : ''}
                        </div>
                      );
                    })()
                  ) : (
                    // レバレッジ保有: 残り期間を表示
                    <div
                      style={{
                        ...styles.holdingExpiry,
                        ...(holding.isExpiringSoon ? styles.expiryWarning : styles.expiryNormal),
                      }}
                    >
                      {holding.isExpiringSoon ? '⚠️ ' : ''}残り{holding.daysRemaining}日
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* FXポジション */}
        {hasFX && (
          <div style={hasFunds ? styles.fxSection : {}}>
            <div style={styles.fxSectionTitle}>
              📈 FXポジション ({fxPositions.length}件)
            </div>
            {fxPositions.map((pos) => (
              <div
                key={pos.id}
                style={styles.fxCard}
              >
                <div style={styles.holdingIcon}>{pos.pair?.symbol || '📈'}</div>
                <div style={styles.holdingInfo}>
                  <div style={styles.holdingName}>
                    {pos.pair?.name || pos.pairId}
                    <span style={{
                      ...styles.fxDirection,
                      ...(pos.direction === 'long' ? styles.fxDirectionLong : styles.fxDirectionShort),
                    }}>
                      {pos.direction === 'long' ? 'ロング' : 'ショート'} ×{pos.leverage}
                    </span>
                  </div>
                  <div style={styles.holdingDetails}>
                    証拠金: {pos.margin.toLocaleString()}G | レート: {pos.entryRate.toFixed(4)} → {pos.currentRate.toFixed(4)}
                  </div>
                </div>
                <div style={styles.holdingValue}>
                  <div style={styles.holdingAmount}>
                    {pos.estimatedValue.toLocaleString()}G
                  </div>
                  <div
                    style={{
                      ...styles.holdingPnl,
                      color: pos.pnl >= 0 ? '#4caf50' : '#f44336',
                    }}
                  >
                    {pos.pnl >= 0 ? '+' : ''}{pos.pnl.toLocaleString()}G
                    ({pos.pnlPercent >= 0 ? '+' : ''}{pos.pnlPercent.toFixed(1)}%)
                  </div>
                  <div style={{
                    ...styles.holdingExpiry,
                    color: pos.isNearLiquidation ? '#f44336' : '#888',
                  }}>
                    {pos.isNearLiquidation ? '⚠️ ロスカット注意' : `${pos.daysOpen}日保有中`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // 取引履歴
  const renderHistory = () => {
    if (history.length === 0) {
      return (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📜</div>
          <div>取引履歴がありません</div>
        </div>
      );
    }

    return (
      <div style={styles.historyList}>
        {history.map((item, index) => (
          <div key={index} style={styles.historyItem}>
            <div style={styles.historyIcon}>{item.fundIcon}</div>
            <div style={styles.historyInfo}>
              <div style={styles.historyAction}>
                <span style={{
                  color: item.isFX
                    ? (item.action === 'open_fx' ? '#ff9800' : '#9c27b0')
                    : (item.action === 'buy' ? '#4caf50' : '#f44336'),
                }}>
                  {item.actionText}
                </span>
                {' '}{item.fundName}
                {item.isFX ? (
                  <span> {item.units.toLocaleString()}G証拠金 ×{item.leverage}</span>
                ) : (
                  <span> {item.units}口</span>
                )}
              </div>
              <div style={styles.historyDate}>
                {typeof item.date === 'number' && item.date > 1000000000000
                  ? new Date(item.date).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
                  : `${item.date}日目`} | {item.isFX ? 'スプレッド' : '手数料'}: {item.fee.toLocaleString()}G
              </div>
            </div>
            <div style={styles.historyAmount}>
              <div style={styles.historyPrice}>
                {item.isFX ? (
                  <span>レート: {item.price.toFixed(4)}</span>
                ) : (
                  <span>{item.price.toLocaleString()}G/口</span>
                )}
              </div>
              {item.pnl !== undefined && (
                <div
                  style={{
                    ...styles.historyPnl,
                    color: item.pnl >= 0 ? '#4caf50' : '#f44336',
                  }}
                >
                  {item.pnl >= 0 ? '+' : ''}{item.pnl.toLocaleString()}G
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 分析
  const renderAnalysis = () => (
    <div style={styles.analysisSection}>
      {/* 累計損益 */}
      <div style={styles.analysisCard}>
        <div style={styles.analysisTitle}>累計損益</div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>確定損益</span>
          <span
            style={{
              ...styles.statValue,
              color: cumulativePnL.realizedPnL >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {cumulativePnL.realizedPnL >= 0 ? '+' : ''}{cumulativePnL.realizedPnL.toLocaleString()}G
          </span>
        </div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>含み損益</span>
          <span
            style={{
              ...styles.statValue,
              color: cumulativePnL.unrealizedPnL >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {cumulativePnL.unrealizedPnL >= 0 ? '+' : ''}{cumulativePnL.unrealizedPnL.toLocaleString()}G
          </span>
        </div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>総手数料・維持費</span>
          <span style={styles.statValue}>-{cumulativePnL.totalFees.toLocaleString()}G</span>
        </div>
        <div style={{ ...styles.statRow, borderTop: '1px solid #333', paddingTop: '8px', marginTop: '8px' }}>
          <span style={styles.statLabel}>純損益</span>
          <span
            style={{
              ...styles.statValue,
              fontSize: '18px',
              color: cumulativePnL.netPnL >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {cumulativePnL.netPnL >= 0 ? '+' : ''}{cumulativePnL.netPnL.toLocaleString()}G
          </span>
        </div>
      </div>

      {/* 投資統計 */}
      <div style={styles.analysisCard}>
        <div style={styles.analysisTitle}>投資統計</div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>総投資回数</span>
          <span style={styles.statValue}>{cumulativePnL.totalInvestments}回</span>
        </div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>勝率</span>
          <span style={styles.statValue}>{cumulativePnL.winRate.toFixed(1)}%</span>
        </div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>累計維持コスト</span>
          <span style={styles.statValue}>{investmentData.totalMaintenancePaid?.toLocaleString() || 0}G</span>
        </div>
        <div style={styles.statRow}>
          <span style={styles.statLabel}>投資経験</span>
          <span style={styles.statValue}>{investmentData.experienceCount || 0}回</span>
        </div>
      </div>

      {/* 属性分布 */}
      {Object.keys(attributeDistribution).length > 0 && (
        <div style={styles.analysisCard}>
          <div style={styles.analysisTitle}>属性分布</div>
          <div style={styles.distributionBar}>
            {Object.entries(attributeDistribution).map(([attr, data]) => (
              <div
                key={attr}
                style={{
                  width: `${data.percent}%`,
                  backgroundColor: attributeColors[attr] || '#666',
                }}
              />
            ))}
          </div>
          <div style={styles.distributionLegend}>
            {Object.entries(attributeDistribution).map(([attr, data]) => (
              <div key={attr} style={styles.legendItem}>
                <div
                  style={{
                    ...styles.legendColor,
                    backgroundColor: attributeColors[attr] || '#666',
                  }}
                />
                <span>{attr} {data.percent.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      {/* サマリー */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>保有資産</div>
          <div style={styles.summaryValue}>
            {summary.holdingsCount > 0 && `📊${summary.holdingsCount}`}
            {summary.holdingsCount > 0 && fxPositions.length > 0 && ' / '}
            {fxPositions.length > 0 && `📈${fxPositions.length}`}
            {summary.holdingsCount === 0 && fxPositions.length === 0 && '0件'}
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>総評価額</div>
          <div style={styles.summaryValue}>{combinedSummary.totalValue.toLocaleString()}G</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>評価損益</div>
          <div
            style={{
              ...styles.summaryValue,
              color: combinedSummary.totalPnl >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {combinedSummary.totalPnl >= 0 ? '+' : ''}{combinedSummary.totalPnl.toLocaleString()}G
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>損益率</div>
          <div
            style={{
              ...styles.summaryValue,
              color: combinedSummary.totalPnlPercent >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {combinedSummary.totalPnlPercent >= 0 ? '+' : ''}{combinedSummary.totalPnlPercent.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* サブタブ */}
      <div style={styles.subTabs}>
        <button
          style={{
            ...styles.subTab,
            ...(activeSubTab === 'holdings' ? styles.subTabActive : styles.subTabInactive),
          }}
          onClick={() => setActiveSubTab('holdings')}
        >
          保有一覧
        </button>
        <button
          style={{
            ...styles.subTab,
            ...(activeSubTab === 'history' ? styles.subTabActive : styles.subTabInactive),
          }}
          onClick={() => setActiveSubTab('history')}
        >
          取引履歴
        </button>
        <button
          style={{
            ...styles.subTab,
            ...(activeSubTab === 'analysis' ? styles.subTabActive : styles.subTabInactive),
          }}
          onClick={() => setActiveSubTab('analysis')}
        >
          分析
        </button>
      </div>

      {/* コンテンツ */}
      {activeSubTab === 'holdings' && renderHoldings()}
      {activeSubTab === 'history' && renderHistory()}
      {activeSubTab === 'analysis' && renderAnalysis()}
    </div>
  );
};

export default Portfolio;
